import { List, ListItem, ListItemText, Stack } from "@mui/material";
import LoadingButton from "../../../../components/ui/LoadingButton";
import StatusPill, { StatusPillLevel } from "../../../../components/ui/StatusPill";
import { useIntl } from "../../../../contexts/IntlContext";
import { LivePeriodPartResult, LivePeriodResult } from "../../../../hooks/useLivePeriod";

type Props = {
  result: LivePeriodResult;
  onRetryPart: (part: LivePeriodPartResult) => void;
  onAbandonPart: (part: LivePeriodPartResult) => void;
  retryingSlotId: number | null;
  abandoningSlotId: number | null;
};

const formatDateBR = (date: string) => {
  const [y, m, d] = String(date || "").split("-");
  if (!y || !m || !d) return date;
  return `${d}/${m}/${y}`;
};

const formatTime = (value: string) => String(value || "").slice(11, 16) || value;

const statusLevel: Record<string, StatusPillLevel> = {
  CREATED: "success",
  FAILED: "error",
  SKIPPED: "warning",
  ABANDONED: "neutral",
};

/**
 * Gerenciamento pós-criação: uma linha por PARTE (não por dia — dias com Parte 1/Parte 2 aparecem
 * separados, cada um com seu próprio status), com retry manual e desistência para partes com falha
 * ou puladas. Usa o `StatusPill` compartilhado (mesmo padrão de `LivesManager`/`OwnerYoutubeConnection`)
 * em vez de um Chip solto, para ficar consistente com o resto do app.
 */
export default function LivePeriodScheduleList({
  result,
  onRetryPart,
  onAbandonPart,
  retryingSlotId,
  abandoningSlotId,
}: Props) {
  const intl = useIntl();

  return (
    <List dense disablePadding>
      {result.days.flatMap((day) =>
        day.parts.map((part) => {
          const label = part.part
            ? `${formatDateBR(day.date)} — Parte ${part.part}`
            : formatDateBR(day.date);
          const isRetrying = part.slotId != null && retryingSlotId === part.slotId;
          const isAbandoning = part.slotId != null && abandoningSlotId === part.slotId;
          const canAct = part.slotId != null && (part.status === "FAILED" || part.status === "SKIPPED");
          return (
            <ListItem
              key={part.slotId ?? `${part.start}::${part.end}`}
              disableGutters
              secondaryAction={
                canAct ? (
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      size="small"
                      loading={isRetrying}
                      disabled={isAbandoning}
                      onClick={() => onRetryPart(part)}
                    >
                      {intl.formatMessage({ id: "liveStreams.period.retry" })}
                    </LoadingButton>
                    <LoadingButton
                      size="small"
                      color="inherit"
                      loading={isAbandoning}
                      disabled={isRetrying}
                      onClick={() => onAbandonPart(part)}
                    >
                      {intl.formatMessage({ id: "liveStreams.period.giveUp" })}
                    </LoadingButton>
                  </Stack>
                ) : undefined
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" component="span">
                    <span>
                      {label} {formatTime(part.start)}–{formatTime(part.end)}
                    </span>
                    <StatusPill
                      size="small"
                      label={intl.formatMessage({
                        id: `liveStreams.period.status.${part.status ?? "SCHEDULED"}`,
                      })}
                      level={statusLevel[part.status ?? ""] ?? "neutral"}
                    />
                  </Stack>
                }
                secondary={part.error}
              />
            </ListItem>
          );
        }),
      )}
    </List>
  );
}
